import { useState, useEffect, useRef } from "preact/hooks";
import { PaymentGateway, PaymentNotification, PaymentStatus, ProcessorRef, TransactionStatusPoller } from "../../../../../../services/payment-gateway";
import Config from "../../../../config";

export type Steps = 'start' | 'wait' | 'error' | 'pending' | 'success';

export interface UseWithdrawTransactionArgs {
    provider: ProcessorRef;
}

export const useWithdrawTransaction = ({ provider }: UseWithdrawTransactionArgs) => {
    const service = useRef(new PaymentGateway(Config.softionAPI));
    const poller = useRef<TransactionStatusPoller | null>(null);

    const [step, setStep] = useState<Steps>('start');
    const [error, setError] = useState<string | null>(null);
    const [statusUrl, setStatusUrl] = useState<string | null>(null);

    const reset = () => {
        if (step == "success" || step == "error")
            setStep("start");
    }

    useEffect(() => {
        return () => {
            poller.current?.stop();
        };
    }, []);

    const handleTransactionStatus = (status: PaymentNotification) => {
        if (status.status === PaymentStatus.SUCCESS) {
            setStep('success');
        } else if (status.status === PaymentStatus.FAILED) {
            setError(status.error.source + ": " + status.error.msg);
            setStep('error');
        }
    };

    useEffect(() => {
        if (step === 'pending' && statusUrl) {
            poller.current = new TransactionStatusPoller(statusUrl);
            poller.current.addListener(handleTransactionStatus);
            poller.current.start();
        } else {
            poller.current?.stop();
        }
    }, [step, statusUrl]);

    const initiateTransaction = async (amount: string) => {
        setStep('wait');
        setError(null);

        try {
            const response = await service.current.requestWithdraw(provider.provider, provider.processor, amount);

            if (response.status === 'error') {
                setError(response.error.source + ": " + response.error.msg);
                setStep('error');
                return;
            }

            if (response.data.statusUrl) {
                setStatusUrl(response.data.statusUrl);
                setStep('pending');
            } else {
                setStep('success');
            }
        } catch (e) {
            setError("An unexpected error occurred.");
            setStep('error');
        }
    };

    return {
        step,
        error,
        statusUrl,
        initiateTransaction,
        handleTransactionStatus,
        reset
    };
};